import {
  AtSign,
  CheckCircle2,
  CheckSquare,
  ClipboardCheck,
  ClipboardList,
  FileText,
  FolderPlus,
  MessageSquare,
  UserPlus,
  type LucideIcon,
} from 'lucide-react';
import type { ActivityType, GroupedActivity } from '@/lib/api';

/**
 * How an Activity item looks and where it goes, in one place: the Activity
 * page, the Dashboard's Recent Activity and the project Overview's Activity
 * card all read these, so an item looks and links the same everywhere.
 *
 * Direct messages aren't Activity: they're the chat applet's.
 */
export const ACTIVITY_DISPLAY: Record<ActivityType, { icon: LucideIcon; color: string; bgColor: string }> = {
  MENTION: { icon: AtSign, color: 'text-blue-600', bgColor: 'bg-blue-100' },
  REPLY: { icon: MessageSquare, color: 'text-green-600', bgColor: 'bg-green-100' },
  TASK_ASSIGNED: { icon: CheckSquare, color: 'text-orange-600', bgColor: 'bg-orange-100' },
  TASK_COMPLETED: { icon: CheckCircle2, color: 'text-emerald-600', bgColor: 'bg-emerald-100' },
  DISCUSSION_CREATED: { icon: FileText, color: 'text-indigo-600', bgColor: 'bg-indigo-100' },
  FILE_SHARED: { icon: FolderPlus, color: 'text-cyan-600', bgColor: 'bg-cyan-100' },
  PROJECT_ADDED: { icon: UserPlus, color: 'text-pink-600', bgColor: 'bg-pink-100' },
  FORM_ASSIGNED: { icon: ClipboardList, color: 'text-amber-600', bgColor: 'bg-amber-100' },
  FORM_SUBMITTED: { icon: ClipboardCheck, color: 'text-teal-600', bgColor: 'bg-teal-100' },
};

export function activityLink(activity: GroupedActivity): string {
  switch (activity.entityType) {
    case 'MESSAGE':
      return activity.projectId
        ? `/projects/${activity.projectId}/discussions/${activity.entityId}`
        : '#';
    case 'TASK':
      // The project's Tasks screen opens ?taskId= in its modal
      return activity.projectId
        ? `/projects/${activity.projectId}/tasks${activity.entityId ? `?taskId=${activity.entityId}` : ''}`
        : '#';
    case 'PROJECT':
      return `/projects/${activity.entityId}`;
    case 'FORM':
      // Assignees go to My Forms (which opens the right response); staff land in the manager.
      return activity.type === 'FORM_SUBMITTED'
        ? `/forms/manage/${activity.entityId}?tab=responses`
        : '/forms';
    default:
      return '#';
  }
}

/** "3 replies" for a group of several; empty for a single item. */
export function activityGroupLabel(type: ActivityType, count: number): string {
  if (count === 1) return '';
  switch (type) {
    case 'REPLY': return `${count} replies`;
    case 'MENTION': return `${count} mentions`;
    case 'TASK_ASSIGNED': return `${count} task updates`;
    case 'FORM_ASSIGNED': return `${count} form requests`;
    case 'FORM_SUBMITTED': return `${count} form submissions`;
    case 'TASK_COMPLETED': return `${count} completed`;
    case 'DISCUSSION_CREATED': return `${count} discussions`;
    default: return `${count} activities`;
  }
}
